import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Music2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/lessons', label: 'Lessons' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/location', label: 'Location' },
  { to: '/testimonials', label: 'Testimonials' },
  { to: '/faq', label: 'FAQ' },
  { to: '/resources', label: 'Resources' },
  { to: '/contact', label: 'Contact' },
];

function buildMailtoBook(settings) {
  const subject = encodeURIComponent('Singvolve: Booking Enquiry - Singing Lessons');
  const body = encodeURIComponent(`I'd like to book singing lessons.\n\nName - Pupil: \nName - Parent/Carer (If applicable): \nEmail: \nTelephone number: \nAre you available on a Tuesday or Thursday evening between 5-7pm to have a 30 minute lesson at the Bristol Cathedral Choir School (BCCS) in Bristol: \nWhat style of music would you like to try (optional): `);
  return `mailto:${settings.adminEmail}?subject=${subject}&body=${body}`;
}

function buildMailtoTaster(settings) {
  const subject = encodeURIComponent('Singvolve: Free Taster Session Enquiry');
  const body = encodeURIComponent(`I'd like to enquire about the free taster session on ${settings.tasterDate} at ${settings.tasterTime}.\n\nName: \nEmail: \nTelephone: \nAge of student (if child): `);
  return `mailto:${settings.adminEmail}?subject=${subject}&body=${body}`;
}

export default function Header() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const settings = useSiteSettings();

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/90 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3 lg:py-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
            <img src="/logo.jpg" alt="Singvolve" className="h-[120px] lg:h-[180px] w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 text-sm font-medium transition-colors relative
                  ${location.pathname === link.to ? 'text-[#4B1D5E]' : 'text-gray-600 hover:text-[#4B1D5E]'}
                  after:absolute after:left-3 after:right-3 after:bottom-1 after:h-0.5 after:bg-[#4B1D5E] after:transition-all
                  ${location.pathname === link.to ? 'after:w-[calc(100%-24px)]' : 'after:w-0 hover:after:w-[calc(100%-24px)]'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {settings.tasterEnabled && (
              <a href={buildMailtoTaster(settings)} data-testid="header-taster-cta">
                <Button variant="outline" className="border-[#4B1D5E] text-[#4B1D5E] hover:bg-[#F5F0F7] rounded-full text-sm">
                  Free Taster
                </Button>
              </a>
            )}
            <a href={buildMailtoBook(settings)} data-testid="header-book-cta">
              <Button className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-full text-sm">
                Book Lessons
              </Button>
            </a>
            <Link to="/portal-login" data-testid="header-portal-link">
              <Button variant="ghost" className="text-[#4B1D5E] hover:bg-[#F5F0F7] text-sm">
                Student Login
              </Button>
            </Link>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-6 w-6 text-[#4B1D5E]" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b">
                  <img src="/logo.jpg" alt="Singvolve" className="h-16 w-auto" />
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                </div>
                <nav className="flex flex-col p-4 gap-1 overflow-y-auto flex-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setOpen(false)}
                      className={`px-4 py-3 rounded-lg text-base font-medium transition-colors
                        ${location.pathname === link.to ? 'bg-[#F5F0F7] text-[#4B1D5E]' : 'text-gray-600 hover:bg-gray-50 hover:text-[#4B1D5E]'}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/portal-login"
                    onClick={() => setOpen(false)}
                    className="px-4 py-3 rounded-lg text-base font-medium text-[#4B1D5E] hover:bg-[#F5F0F7] flex items-center gap-2"
                  >
                    <Music2 className="h-4 w-4" /> Student Login
                  </Link>
                </nav>
                <div className="p-4 border-t space-y-3">
                  {settings.tasterEnabled && (
                    <a href={buildMailtoTaster(settings)} className="block" onClick={() => setOpen(false)}>
                      <Button variant="outline" className="w-full border-[#4B1D5E] text-[#4B1D5E] rounded-full">
                        Free Taster Session
                      </Button>
                    </a>
                  )}
                  <a href={buildMailtoBook(settings)} className="block" onClick={() => setOpen(false)}>
                    <Button className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-full">
                      Book Lessons
                    </Button>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
